import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { FiX, FiAlertCircle } from 'react-icons/fi';

import { useAuth } from '../../contexts/AuthContext';

function DocumentViewer({ documentId, filename, onClose }) {
  const { isDarkMode } = useAuth();
  const [documentUrl, setDocumentUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    const loadDocument = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const token = localStorage.getItem('msb-token');
        if (!token) {
          throw new Error('Keine Authentifizierung gefunden. Bitte melden Sie sich an.');
        }

        // Fetch das Dokument mit Authorization Header
        const response = await fetch(`/api/ki-workspace/chat/documents/${documentId}/view`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        // Konvertiere Response zu Blob
        const blob = await response.blob();

        // Erstelle Object URL für den Blob
        const url = URL.createObjectURL(blob);
        setDocumentUrl(url);
      } catch (err) {
        console.error('Fehler beim Laden des Dokuments:', err);
        setError(err.message);
        toast.error(`Fehler beim Laden: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (documentId) {
      loadDocument();
    }

    // Cleanup: Revoke Object URL beim Unmount
    return () => {
      if (documentUrl) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [documentId]);

  // ESC-Taste zum Schließen
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Hinweis: Automatische Text-Suche wurde entfernt, da sie in PDFs nicht zuverlässig funktioniert.
  // Stattdessen wird der Text beim Klick auf ein Snippet in die Zwischenablage kopiert,
  // sodass der Benutzer manuell mit Strg+F suchen kann.

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
      onClick={onClose}
    >
      <div
        className={`relative w-[95vw] h-[95vh] rounded-lg shadow-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`flex flex-col p-4 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}
        >
          <div className="flex items-center justify-between">
            <h3
              className={`text-lg font-semibold truncate flex-1 mr-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            >
              {filename}
            </h3>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-700'}`}
              title="Schließen (ESC)"
            >
              <FiX size={20} />
            </button>
          </div>
          {/* Hilfsmeldung für STRG-F */}
          <div
            className={`mt-2 p-2 rounded text-xs flex items-start gap-2 ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}
          >
            <span className="flex-shrink-0">💡</span>
            <span>
              <strong>Textsuche:</strong> Nutze{' '}
              <kbd className="px-1 py-0.5 rounded bg-gray-700 dark:bg-gray-600 text-white text-xs font-mono">
                Strg+F
              </kbd>{' '}
              (Windows/Linux) oder{' '}
              <kbd className="px-1 py-0.5 rounded bg-gray-700 dark:bg-gray-600 text-white text-xs font-mono">
                Cmd+F
              </kbd>{' '}
              (Mac) im geöffneten Dokument. Der Browser-PDF-Viewer unterstützt die Volltextsuche.
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="w-full h-[calc(100%-130px)] p-4">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div
                  className={`animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4 ${isDarkMode ? 'border-blue-400' : 'border-blue-600'}`}
                />
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                  Dokument wird geladen...
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md">
                <FiAlertCircle
                  className={`mx-auto mb-4 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}
                  size={48}
                />
                <h4
                  className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  Fehler beim Laden
                </h4>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{error}</p>
              </div>
            </div>
          )}

          {documentUrl && !isLoading && !error && (
            <iframe
              ref={iframeRef}
              src={documentUrl}
              className="w-full h-full rounded border-0"
              title={filename}
              style={{ backgroundColor: isDarkMode ? '#1f2937' : '#ffffff' }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentViewer;
